import * as Linking from "expo-linking";
import { LinkingOptions, getStateFromPath } from "@react-navigation/native";
import { URL } from "react-native-url-polyfill";
import { NativeModules } from "react-native";
import type { FirebaseMessagingTypes } from "@react-native-firebase/messaging/lib";
import config from "../../config";
import { RootStackParamList } from "../types";
import { linkingConfig as platformLinkingConfig, historyRules as platformHistoryRules } from "./linkingConfig";
import { dcPrefix, memberPrefix, toPathSegment } from "./utils";
import { ResultState } from "./types";

const linkingConfig = {
  screens: {
    Activities: dcPrefix("activities"),
    Activity: dcPrefix("activities/:code"),
    ActivityRequestPoints: dcPrefix("activities/:code/request-points"),
    ActivityReview: dcPrefix("activity-reviews/:id"),
    MyActivity: dcPrefix("my-activities/:id"),
    MyActivityManageRequest: dcPrefix("my-activities/:id/manage-request"),
    MyActivityHistory: dcPrefix("my-activities"),
    ActivityReviewList: dcPrefix("activity-reviews"),
    EventDetails: memberPrefix("events/:slug"),
    EventRegistration: memberPrefix("events/:slug/register"),
    EventConfirmation: memberPrefix("events/:slug/confirmation"),
    EventTicket: memberPrefix("events/:slug/ticket"),
    Enroll: {
      path: dcPrefix("enroll"),
      screens: {
        Overview: "overview",
        Terms: "terms",
        AccountDetails: "account-details",
        Success: "success",
      },
    },
    Dues: {
      path: memberPrefix("dues"),
      screens: {
        DuesOverview: "overview",
        DuesProxyOverview: "proxy-overview",
        DuesReview: "review",
        DuesProxyReview: "proxy-review",
        DuesSummary: "summary",
        AssignProxy: "assign-proxy",
      },
    },

    ...platformLinkingConfig.screens,
  },
};

export function jobUrl(jobId: string): string {
  return `https://${config.jobsDomain}/detail/${jobId}`;
}

type Messaging = {
  (): Pick<FirebaseMessagingTypes.Module, "getInitialNotification" | "onNotificationOpenedApp">;
};

let messaging: Messaging = () => ({
  getInitialNotification: () => Promise.resolve(null),
  onNotificationOpenedApp: () => () => {},
});

if (NativeModules.RNFBAppModule) {
  const { default: defaultMessaging } = require("@react-native-firebase/messaging");
  messaging = defaultMessaging;
}

function prefixPathWithHost(url: string) {
  const u = new URL(url);

  u.pathname = [toPathSegment(u.hostname), u.pathname].join("/");

  return u.toString();
}

const injectHistory = (path: string, state: ResultState) =>
  platformHistoryRules.find((rule) => rule.applicable(path))?.apply(state) || state;

export const linking: LinkingOptions<RootStackParamList> = {
  prefixes: [
    Linking.createURL("/"),
    ...[config.webDomain, config.jobsDomain, config.accountDomain].map((domain) => `https://${domain}`),
  ],
  config: linkingConfig,
  async getInitialURL() {
    let url = await Linking.getInitialURL();
    if (!url) {
      const message = await messaging().getInitialNotification();
      url = (message?.data?.link as string) || null;
    }

    return url && prefixPathWithHost(url);
  },
  getStateFromPath(path, options) {
    const state = getStateFromPath(path, options);
    if (state) return injectHistory(path, state);
  },
  subscribe(listener: (url: string) => void) {
    function listen(url: string) {
      listener(prefixPathWithHost(url));
    }

    const urlListener = Linking.addEventListener("url", ({ url }) => listen(url));

    const unsubscribeFirebaseNotification = messaging().onNotificationOpenedApp((message) => {
      const url = message?.data?.link as string;

      if (url) listen(url);
    });

    return () => {
      urlListener.remove();
      unsubscribeFirebaseNotification();
    };
  },
};
